<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";

  export let items: { value: string; label: string }[] = [];
  export let value = "";
  export let placeholder = "";
  export let testid = "";
  /** Optional id forwarded to the input so a parent <label for> can bind it. */
  export let id = "";

  const listId = `ac-list-${Math.random().toString(36).slice(2)}`;

  const dispatch = createEventDispatcher<{ change: string }>();

  /** Internal display text shown in the input. */
  let text = "";
  let focused = false;
  let open = false;

  /** Resolve the display label for a given committed value. */
  function labelForValue(v: string): string {
    const hit = items.find((it) => it.value === v);
    return hit ? hit.label : v;
  }

  onMount(() => {
    text = value ? labelForValue(value) : "";
  });

  // Keep display text in sync when the parent changes `value` externally
  // (e.g. a row filter icon sets it). Only when not actively editing.
  $: if (!focused) {
    const next = value ? labelForValue(value) : "";
    if (next !== text) text = next;
  }

  $: suggestions = (() => {
    const q = text.trim().toLowerCase();
    if (q === "") return [];
    return items
      .filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          it.value.toLowerCase().includes(q),
      )
      .slice(0, 50);
  })();

  $: showDropdown = open && focused && text.trim() !== "" && suggestions.length > 0;

  function onInput() {
    // Free-form typing commits the raw text as the value so arbitrary
    // ids/labels still work without picking a suggestion.
    value = text;
    open = true;
  }

  function commit() {
    value = text;
    open = false;
    dispatch("change", value);
  }

  function pick(it: { value: string; label: string }) {
    value = it.value;
    text = it.label;
    open = false;
    dispatch("change", value);
  }

  function onKeydown(ev: KeyboardEvent) {
    if (ev.key === "Escape") {
      open = false;
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      commit();
    }
  }

  function onFocus() {
    focused = true;
    open = true;
  }

  function onBlur() {
    focused = false;
    // Delay so a suggestion click (mousedown→click) can fire first.
    setTimeout(() => {
      open = false;
    }, 120);
    commit();
  }
</script>

<div class="ac-wrap">
  <input
    {id}
    type="text"
    bind:value={text}
    {placeholder}
    data-testid={testid}
    autocomplete="off"
    role="combobox"
    aria-controls={listId}
    aria-expanded={showDropdown}
    aria-autocomplete="list"
    on:input={onInput}
    on:focus={onFocus}
    on:blur={onBlur}
    on:keydown={onKeydown}
  />
  {#if showDropdown}
    <ul class="ac-list" id={listId} role="listbox">
      {#each suggestions as s (s.value)}
        <li role="presentation">
          <button
            type="button"
            class="ac-option"
            role="option"
            aria-selected={s.value === value}
            on:mousedown|preventDefault={() => pick(s)}
          >
            {s.label}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .ac-wrap {
    position: relative;
    display: inline-block;
  }
  input {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  .ac-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 20;
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 4px;
    max-height: 14rem;
    overflow-y: auto;
  }
  .ac-option {
    display: block;
    width: 100%;
    text-align: left;
    padding: 0.35rem 0.5rem;
    background: transparent;
    color: #f3f4f6;
    border: none;
    border-radius: 0;
    font-size: 0.85rem;
    cursor: pointer;
  }
  .ac-option:hover,
  .ac-option[aria-selected="true"] {
    background: #0f172a;
  }
</style>
